/**
 * Workspace naming utility for consistent workspace name generation
 * Following Software Architect best practices for clean, maintainable utilities
 */

/**
 * Configuration for workspace naming
 */
export interface WorkspaceNamingConfig {
  maxLength: number;
  separator: string;
  includeTimestamp: boolean;
  timestampFormat: 'short' | 'full';
  fallbackPrefix: string;
}

/**
 * Default configuration for workspace naming
 */
export const DEFAULT_NAMING_CONFIG: WorkspaceNamingConfig = {
  maxLength: 100,
  separator: ' ',
  includeTimestamp: false,
  timestampFormat: 'short',
  fallbackPrefix: 'Workspace'
};

/**
 * Generate a consistent workspace name from search parameters
 * 
 * Format: "{keyword} {language} {location}" or "{keyword} research"
 * Example: "React SEO English United States" -> "React SEO English United States"
 * Example: "React SEO" -> "React SEO research"
 * 
 * @param keyword - The search keyword/query
 * @param language - The language (optional)
 * @param location - The location (optional)
 * @param config - Naming configuration
 * @returns Generated workspace name
 */
export function generateWorkspaceName(
  keyword: string,
  language?: string,
  location?: string,
  config: WorkspaceNamingConfig = DEFAULT_NAMING_CONFIG
): string {
  try {
    // Clean and validate inputs
    const cleanKeyword = cleanText(keyword);
    const cleanLanguage = language ? cleanText(language) : null;
    const cleanLocation = location ? cleanText(location) : null;

    if (!cleanKeyword) {
      return generateFallbackName(config);
    }

    // Build name components
    const components: string[] = [cleanKeyword];

    // Add language if provided and not default
    if (cleanLanguage && cleanLanguage !== 'English' && cleanLanguage !== 'english') {
      components.push(cleanLanguage);
    }

    // Add location if provided
    if (cleanLocation) {
      components.push(cleanLocation);
    }

    // If only keyword (no language or location), add "research" for clarity
    if (components.length === 1) {
      components.push('research');
    }

    // Join components
    let workspaceName = components.join(config.separator);

    // Add timestamp if configured
    if (config.includeTimestamp) {
      const timestamp = generateTimestamp(config.timestampFormat);
      workspaceName += `${config.separator}${timestamp}`;
    }

    // Truncate if necessary
    if (workspaceName.length > config.maxLength) {
      workspaceName = truncateWorkspaceName(workspaceName, config.maxLength, config.separator);
    }

    // Final validation
    if (!workspaceName || workspaceName.trim().length === 0) {
      return generateFallbackName(config);
    }

    return workspaceName;

  } catch (error) {
    console.error('[generateWorkspaceName] Error generating workspace name:', error);
    return generateFallbackName(config);
  }
}

/**
 * Clean text by removing special characters and normalizing spaces
 */
function cleanText(text: string): string {
  if (!text || typeof text !== 'string') return '';
  
  return text
    .trim()
    .replace(/[^\w\s-]/g, '') // Remove special characters except hyphens
    .replace(/\s+/g, ' ') // Normalize multiple spaces
    .replace(/^-+|-+$/g, '') // Remove leading/trailing hyphens
    .trim();
}

/**
 * Generate timestamp string
 */
function generateTimestamp(format: 'short' | 'full'): string {
  const now = new Date();
  
  if (format === 'full') {
    return now.toISOString().slice(0, 19).replace(/[T:]/g, '').replace(/-/g, '');
  }
  
  // Short format: MMDD
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${month}${day}`;
}

/**
 * Truncate workspace name intelligently
 */
function truncateWorkspaceName(name: string, maxLength: number, separator: string): string {
  if (name.length <= maxLength) return name;

  // Try to truncate at word boundaries
  const parts = name.split(separator);
  let truncated = parts[0]; // Always keep the first part (keyword)
  
  for (let i = 1; i < parts.length; i++) {
    const nextPart = `${truncated}${separator}${parts[i]}`;
    if (nextPart.length <= maxLength) {
      truncated = nextPart;
    } else {
      break;
    }
  }

  // If still too long, truncate the first part
  if (truncated.length > maxLength) {
    truncated = truncated.slice(0, maxLength - 3) + '...';
  }

  return truncated;
}

/**
 * Generate fallback name when primary generation fails
 */
function generateFallbackName(config: WorkspaceNamingConfig): string {
  const timestamp = config.includeTimestamp ? 
    `${config.separator}${generateTimestamp(config.timestampFormat)}` : '';
  
  return `${config.fallbackPrefix}${timestamp}`;
}

/**
 * Validate workspace name according to workspace validation rules
 */
export function validateWorkspaceName(name: string): {
  valid: boolean;
  error?: string;
} {
  if (!name || typeof name !== 'string') {
    return { valid: false, error: 'Workspace name is required' };
  }

  const trimmedName = name.trim();

  if (trimmedName.length === 0) {
    return { valid: false, error: 'Workspace name cannot be empty' };
  }

  if (trimmedName.length > 100) {
    return { valid: false, error: 'Workspace name must not exceed 100 characters' };
  }

  // Check for invalid characters
  const validPattern = /^[a-zA-Z0-9\s\-_]+$/;
  if (!validPattern.test(trimmedName)) {
    return { 
      valid: false, 
      error: 'Workspace name can only contain letters, numbers, spaces, hyphens, and underscores' 
    };
  }

  return { valid: true };
}

/**
 * Check if a workspace name would be considered a duplicate
 * Note: This is a client-side helper - actual duplicate checking should be done server-side
 */
export function isDuplicateName(name: string, existingNames: string[]): boolean {
  const normalizedName = name.trim().toLowerCase();
  return existingNames.some(existing => 
    existing.trim().toLowerCase() === normalizedName
  );
}

/**
 * Generate a unique workspace name by appending a number if needed
 */
export function generateUniqueWorkspaceName(
  baseName: string,
  existingNames: string[],
  config: WorkspaceNamingConfig = DEFAULT_NAMING_CONFIG
): string {
  let name = baseName;
  let counter = 1;

  while (isDuplicateName(name, existingNames)) {
    const suffix = `${config.separator}${counter}`;
    const nameWithSuffix = `${baseName}${suffix}`;
    
    if (nameWithSuffix.length <= config.maxLength) {
      name = nameWithSuffix;
    } else {
      // Truncate base name to make room for suffix
      const availableLength = config.maxLength - suffix.length;
      const truncatedBase = baseName.slice(0, availableLength);
      name = `${truncatedBase}${suffix}`;
    }
    
    counter++;
    
    // Safety break to prevent infinite loop
    if (counter > 999) {
      name = generateFallbackName({
        ...config,
        includeTimestamp: true,
        timestampFormat: 'full'
      });
      break;
    }
  }

  return name;
}

/**
 * Extract keyword from workspace name for display purposes
 */
export function extractKeywordFromWorkspaceName(workspaceName: string): string {
  if (!workspaceName) return '';
  
  // Remove common suffixes
  const name = workspaceName
    .replace(/\s+(research|analysis|keywords?|study)$/i, '')
    .replace(/\s+\d{4}$/g, '') // Remove year timestamps
    .replace(/\s+\d{2,4}$/g, '') // Remove short timestamps
    .trim();

  // Return first part if multiple words
  const parts = name.split(' ');
  return parts[0] || workspaceName;
}

/**
 * Utility for generating workspace names in different scenarios
 */
export const WorkspaceNaming = {
  generate: generateWorkspaceName,
  validate: validateWorkspaceName,
  generateUnique: generateUniqueWorkspaceName,
  extractKeyword: extractKeywordFromWorkspaceName,
  isDuplicate: isDuplicateName,
  
  // Predefined configurations
  configs: {
    default: DEFAULT_NAMING_CONFIG,
    withTimestamp: {
      ...DEFAULT_NAMING_CONFIG,
      includeTimestamp: true,
      timestampFormat: 'short' as const
    },
    compact: {
      ...DEFAULT_NAMING_CONFIG,
      maxLength: 50,
      separator: '-'
    },
    verbose: {
      ...DEFAULT_NAMING_CONFIG,
      maxLength: 150,
      includeTimestamp: true,
      timestampFormat: 'full' as const
    }
  }
};
